import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setHasActiveCancerDiagnosis } from "store/reducers/user/UserAccountSlice";
import SurveyWrapper from "components/v3/sharedComponents/surveys/SurveyWrapper";
import { PATHS } from "constants/pathConstants";
import { useState } from "react";
import { Box } from "@mui/material";
import { COLORS } from "components/v3/Theme/colors";
import SurveySummary from "../SurveySummary";
import {
  deriveSurveyBlockers,
  surveyOutcomeFromBlockers,
  syncMixpanelProfile,
  trackSurveyAbandonAttempted,
  trackSurveyCompleted
} from "analytics";
import { Model } from "survey-core";

const SURVEY_ID = "detailed-cancer-survey";
const WORKFLOW = "cancer_survey" as const;

const CancerSurvey = (): React.ReactElement => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [showSummary, setShowSummary] = useState<boolean>(false);
  const [summarySurveyModel, setSummarySurveyModel] = useState<Model | null>(
    null
  );

  const syncProfileAfterSurvey = (hasActiveCancer: boolean): void => {
    dispatch(setHasActiveCancerDiagnosis(hasActiveCancer));
    syncMixpanelProfile({ hasActiveCancerDiagnosis: hasActiveCancer });
  };

  const handleOnConfirm = (): void => {
    if (!summarySurveyModel) return;

    const blockers = deriveSurveyBlockers(summarySurveyModel);
    const outcome = surveyOutcomeFromBlockers(blockers);

    trackSurveyCompleted({
      surveyId: SURVEY_ID,
      workflow: WORKFLOW,
      outcome,
      blockers
    });

    const hasActiveCancer =
      blockers.includes("active_cancer") ||
      blockers.includes("uncertain_cancer_status");
    syncProfileAfterSurvey(
      blockers.length === 0 ? false : hasActiveCancer
    );

    setShowSummary(false);
    summarySurveyModel.doComplete();
  };

  const handleOnCompleting = (
    surveyModel: Model,
    options: { allow: boolean }
  ): void => {
    if (!showSummary) {
      options.allow = false;
      setSummarySurveyModel(surveyModel);
      setShowSummary(true);
    }
  };

  const handleOnComplete = (): void => {
    history.push(PATHS.LANDING_PAGE);
  };

  const handleOnSurveyInstantiated = (surveyModel: Model): void => {
    surveyModel.pagePrevText = "Back";
  };

  return (
    <>
      {showSummary && summarySurveyModel && (
        <Box
          sx={{
            backgroundColor: COLORS.WHITE,
            borderRadius: "16px",
            padding: {
              xs: "16px",
              md: "32px 64px 0 64px"
            }
          }}
        >
          <SurveySummary
            onBack={() => {
              trackSurveyAbandonAttempted({
                surveyId: SURVEY_ID,
                workflow: WORKFLOW,
                action: "cancelled_exit"
              });
              setShowSummary(false);
            }}
            onSubmit={handleOnConfirm}
            surveyModel={summarySurveyModel}
          />
        </Box>
      )}
      <Box
        sx={
          showSummary
            ? {
                position: "absolute",
                left: "-10000px",
                top: "auto",
                width: "1px",
                height: "1px",
                overflow: "hidden"
              }
            : {}
        }
      >
        <SurveyWrapper
          containerClassName="detailed-cancer-survey-container"
          onSurveyInstantiated={handleOnSurveyInstantiated}
          onComplete={handleOnComplete}
          onCompleting={handleOnCompleting}
          overrideNextButtonEnabled={true}
          surveyId={SURVEY_ID}
          trackCompletion={false}
        />
      </Box>
    </>
  );
};

export default CancerSurvey;
